import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import type { State } from '#src/components/directOffer/NewOfferBuilder/types.ts';

import type { useStateOffer_anyCard } from './__generated__/useStateOffer.graphql.ts';

export const useStateOffer = <T extends useStateOffer_anyCard>({
  cardsData,
  receiveCards,
  sendCards,
  sendAmount,
  receiveAmount,
}: State<T>) => {
  const { toMonetaryAmount } = useMonetaryAmount();

  const sendMinimumPrice = useMemo(() => {
    if (sendCards.length > 0) {
      return MonetaryAmount.zero;
    }
    return receiveCards.reduce((prev: MonetaryAmount, curr) => {
      const card = cardsData[curr.slug];
      if (!card?.publicMinPrices) return prev;

      const monetaryAmount = toMonetaryAmount(card.publicMinPrices);
      return monetaryAmount.eurCents > prev.eurCents ? monetaryAmount : prev;
    }, MonetaryAmount.zero);
  }, [cardsData, receiveCards, sendCards.length, toMonetaryAmount]);

  const receiveMinimumPrice = useMemo(() => {
    if (receiveCards.length > 0) {
      return MonetaryAmount.zero;
    }
    return sendCards.reduce((prev: MonetaryAmount, curr) => {
      const card = cardsData[curr.slug];
      if (!card?.publicMinPrices) return prev;

      const monetaryAmount = toMonetaryAmount(card.publicMinPrices);
      return monetaryAmount.eurCents > prev.eurCents ? monetaryAmount : prev;
    }, MonetaryAmount.zero);
  }, [cardsData, receiveCards.length, sendCards, toMonetaryAmount]);

  const isTradeForNothing = useMemo(() => {
    return (
      (sendAmount.isPositive && receiveCards.length === 0) ||
      (receiveAmount.isPositive && sendCards.length === 0)
    );
  }, [
    receiveAmount.isPositive,
    receiveCards.length,
    sendAmount.isPositive,
    sendCards.length,
  ]);

  const isValid = useMemo(() => {
    if (sendMinimumPrice.eurCents > sendAmount.eurCents) {
      return false;
    }

    if (isTradeForNothing) return false;

    if (
      sendAmount.isZero &&
      sendCards.length === 0 &&
      receiveCards.length === 0
    ) {
      return false;
    }

    return true;
  }, [
    isTradeForNothing,
    receiveCards.length,
    sendAmount,
    sendCards.length,
    sendMinimumPrice,
  ]);

  const offerAboveMarketValue = useMemo(() => {
    const offerIsCashForSingleCard =
      sendCards.length === 0 &&
      receiveAmount.isZero &&
      receiveCards.length === 1;
    if (!offerIsCashForSingleCard) return false;

    const singleReceivedCard = cardsData[receiveCards[0].slug];
    return (
      !!singleReceivedCard?.priceRange?.max &&
      sendAmount.wei > singleReceivedCard.priceRange.max
    );
  }, [
    cardsData,
    receiveAmount.isZero,
    receiveCards,
    sendAmount.wei,
    sendCards.length,
  ]);

  return {
    isValid,
    isTradeForNothing,
    sendMinimumPrice,
    receiveMinimumPrice,
    offerAboveMarketValue,
  };
};

useStateOffer.fragments = {
  anyCard: gql`
    fragment useStateOffer_anyCard on AnyCardInterface {
      slug
      sport
      priceRange {
        max
      }
      publicMinPrices {
        ...useMonetaryAmount_monetaryAmount
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<useStateOffer_anyCard>,
};
